import React, { useEffect, useState } from 'react'
import { Avatar, Box, Button, Dialog, IconButton, Tooltip, Typography, useTheme } from '@mui/material'

import { useUserStore } from '../../store/user.store'
import { ASSIGNMENT_CONVERT_DUE_TIME_BEFORE, convertToCDNLink, Icons } from '../../utils/utilities'
import DiscussionsCard from '../../components/dashboard-components/DiscussionsCard'
import AnnouncementsCard from '../../components/dashboard-components/AnnouncementsCard'
import PendingOrRecentAssignmentsCard from '../../components/dashboard-components/PendingOrRecentAssignmentsCard'
import CurrentOrRecentExamsCard from '../../components/dashboard-components/CurrentOrRecentExams'
import ClassesCard from '../../components/dashboard-components/ClassesCard'
import StudentPracticeWithSimbaCard from '../../components/user/practice-with-simba/StudentPracticeWithSimbaCard'
import { useAssignmentStore } from '../../store/assignment.store'
import simbaImg from '../../assets/simba-ai/celebration.png'
import { useRoomStore } from '../../store/room.store'

import { observer } from 'mobx-react'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'

function getGreeting ( name ) {
    const hour = new Date().getHours()
    if ( hour < 12 ) return `Hey! ${name?.toLowerCase()}, Good morning.`
    if ( hour < 16 ) return `Hey! ${name?.toLowerCase()}, Good afternoon.`
    return `Hey! ${name?.toLowerCase()}! Good evening.`
}

const getDashboardGridLayout = ( hasSimbaAccess ) => {
    // 1. Define the components and their specific Grid Area names
    const config = [
        { id: 'cc', component: <ClassesCard key='cc' />, area: 'cc', width: 2 },
        { id: 'pwsb', component: <StudentPracticeWithSimbaCard key='pwsb' />, area: 'pwsb', width: 4, permission: hasSimbaAccess },
        { id: 'prac', component: <PendingOrRecentAssignmentsCard key='prac' />, area: 'prac', width: 2 },
        { id: 'cre', component: <CurrentOrRecentExamsCard key='cre' />, area: 'cre', width: 2 },
        { id: 'dc', component: <DiscussionsCard key='dc' />, area: 'dc', width: 2 },
    ]

    // 2. Filter visible components
    const visibleItems = config.filter( item => item.permission !== false )

    // 3. Logic for Desktop Template (MD)
    // We calculate how to fill the 6-column grid. 
    // If Simba is missing, we might want to expand ClassesCard (cc) or shift others.
    let mdTemplate
    if ( hasSimbaAccess ) {
        mdTemplate = `
            "cc cc pwsb pwsb pwsb pwsb"
            "prac prac cre cre dc dc"
        `
    } else {
        // Layout if Simba is hidden: Expand ClassesCard to take more space or adjust row 1
        mdTemplate = `
            "cc cc prac prac cre cre"
            "dc dc dc dc dc dc"
        `
    }

    // 4. Logic for Mobile Template (XS)
    const xsTemplate = visibleItems.map( item => `"${item.area}"` ).join( " " )

    return { visibleItems, mdTemplate, xsTemplate }
}

function getResourceInfo ( classes = 0, assignments = 0 ) {
    let infoDetails = ''



    if ( assignments > 0 ) {
        infoDetails += `You have ${assignments} assignments due.`
        return infoDetails
    }

    if ( classes > 0 ) {
        infoDetails += `You have ${classes} scheduled ${classes === 1 ? 'class' : 'classes'} today.`
    } else {
        infoDetails += `You have no classes scheduled for today. This is an excellent time to prepare for exams, Check for any new study materials uploaded!`
    }
    return infoDetails
}

const assignmentFilterFunc = a => {
    let dueDate = dayjs( a.assignment_created_at ) < dayjs( ASSIGNMENT_CONVERT_DUE_TIME_BEFORE ) ?
        dayjs( dayjs( a.assignment_due_date ).format( "YYYY-MM-DD" ) ).add( 1, 'day' ) :
        dayjs( a.assignment_due_date )
    const pending = +a.submission_exists === 0 && dueDate > dayjs()
    return pending
}

const StudentDashboard = observer( () => {

    const [showTooltip, setShowTooltip] = useState( false )

    const AssignmentStore = useAssignmentStore()
    const UserStore = useUserStore()
    const RoomStore = useRoomStore()

    const { palette, layout, border } = useTheme()

    const setSearchParams = useSearchParams().at( 1 )

    const { visibleItems, mdTemplate, xsTemplate } = getDashboardGridLayout( UserStore.getUser.features_permissions['simba_ai'] )

    const enterSimbaAI = () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'simbaAIOpen', 'true' )
            newParams.set( 'simbaAIMode', 'practice' )
            return newParams
        }, { replace: true } )
    }

    const handleDismiss = () => {
        localStorage.setItem( 'last_psa_dialog_shown', dayjs().format( "YYYY-MM-DD" ) )
        setShowTooltip( false )
    }

    useEffect( () => {

        if ( !UserStore.getUser.features_permissions['simba_ai'] ) return

        const hasSeenHint = localStorage.getItem( 'last_psa_dialog_shown' )

        if ( dayjs() < dayjs( "2026-02-12" ) && ( !hasSeenHint || hasSeenHint !== dayjs().format( "YYYY-MM-DD" ) ) ) {
            setShowTooltip( true )
        }
    }, [UserStore] )

    return (
        <Box flexGrow={1} display="flex" flexDirection="column" overflow="auto" padding={layout.pagePadding}>

            {/* PSA Pop-up */}
            {UserStore.getUser.features_permissions['simba_ai'] && <Dialog
                slotProps={{
                    paper: {
                        sx: {
                            maxWidth: "500px"
                        }
                    }
                }}
                open={showTooltip}
            >
                <Box display="flex" padding="20px" gap="10px" justifyContent="space-between" alignItems="center">
                    <Typography variant='subtitle2' fontSize="12px" component="p" textTransform="uppercase">Introducing Practice Sessions With Simba AI</Typography>
                    <Tooltip title="Dismiss" >
                        <IconButton
                            size="small"
                            onClick={handleDismiss}
                        >
                            {Icons.default.CloseIcon}
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box
                    padding="40px"
                    sx={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%239C92AC66' fill-opacity='0.19'%3E%3Cpath d='M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41zM20 18.6l2.83-2.83 1.41 1.41L21.41 20l2.83 2.83-1.41 1.41L20 21.41l-2.83 2.83-1.41-1.41L18.59 20l-2.83-2.83 1.41-1.41L20 18.59z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                >
                    <Box component="img" src={simbaImg} height="200px" />

                    <Typography gutterBottom textAlign="center" fontSize="32px" lineHeight={1.2} fontWeight={700} component="h1" >
                        Boost Your Skills! New Interactive Practice Sessions are Live!
                    </Typography>
                    <Typography textAlign="center" variant="body2">
                        Move beyond rote learning with AI-generated practice tailored to your syllabus. Build your daily streak, earn points, and take unlimited attempts on any topic. Start practicing now to turn your classroom notes into exam-ready mastery!
                    </Typography>
                    <Button
                        onClick={() => {
                            enterSimbaAI()
                            handleDismiss()
                        }}
                        sx={{ marginTop: "20px" }}
                        variant='contained'
                        size='small'
                        disableElevation
                        endIcon={Icons.default.ArrowOutward}
                    >
                        Start Practice
                    </Button>
                </Box>
            </Dialog>}

            <AnnouncementsCard />


            <Box borderRadius="10px" display="flex" alignItems="center" gap="10px">
                <Avatar src={UserStore.getUser.user_profile ? convertToCDNLink( UserStore.getUser.user_profile ) : null} sx={{ height: "40px", width: "40px", background: palette.primary.dark }}>{Array.from( UserStore.getUser["employee_name"] || "" ).at( 0 )}</Avatar>
                <Box>
                    <Typography display="block" fontSize="14px" variant='subtitle2' textTransform="capitalize">{getGreeting( ( UserStore.getUser["name"] || UserStore.getUser["student_name"] || "" )?.split( " " )[0] )}</Typography>
                    <Typography display="block" lineHeight={1.3} fontSize="12px" color='textSecondary' variant='subtitle2' textTransform="capitalize">{getResourceInfo( RoomStore.getRooms[dayjs().format( "YYYY-MM-DD" )]?.length, AssignmentStore.getDueOrRecentAssignments.filter( assignmentFilterFunc ).length )}</Typography>
                </Box>
            </Box>


            <Box
                display="grid"
                marginTop="20px"
                gap="20px"
                gridTemplateColumns={{ md: "repeat(6, 1fr)", xs: "1fr" }}
                gridTemplateAreas={{
                    md: mdTemplate,
                    xs: xsTemplate
                }}
            >
                {visibleItems.map( ( item ) => (
                    item.component
                ) )}
            </Box>

            {/* <Box display="grid" gridTemplateColumns={{ md: "2fr 1fr", xs: "1fr" }} gap="20px">
                <Box flexGrow={1} display="flex" flexDirection="column" gap="20px">
                    <WeekCalendar shadow />
                    <Box sx={{ display: { xs: "block", md: "none" } }}>
                        <SubjectsCard shadow />
                    </Box>
                    <Box display="grid" gap="20px" gridTemplateColumns={{ md: "1fr 1fr", xs: "1fr" }}>
                        <PendingOrRecentAssignmentsCard />
                        <Box display="grid" gap="20px" gridTemplateColumns="1fr">
                            <CurrentOrRecentExams />
                        </Box>
                    </Box>


                    <Box display="grid" gap="20px" gridTemplateColumns="1fr">
                        <Card elevation={1} >
                            <Box borderRadius="10px" padding="20px" >
                                <Typography gutterBottom fontSize="20px" fontWeight="700">Build your resume</Typography>
                                <Box display="flex" flexDirection={{ md: "row", xs: "column-reverse" }} alignItems={{ md: "center", xs: "stretch" }} justifyContent="space-between">
                                    <Box flexGrow={1} >
                                        {profileProgress !== 100 && <Typography gutterBottom variant="subtitle2">Complete your resume</Typography>}
                                        <Box overflow="hidden" bgcolor="#CBD9CD" width="80%" height="10px" borderRadius="10px">
                                            <Box bgcolor="#22DD3B" borderRadius="0 10px 10px 0" height="100%" width={`${0}%`}></Box>
                                        </Box>
                                        <Box marginTop="20px">
                                            {profileProgress !== 100 && <Button onClick={() => navigate( '/student/resume-builder' )} disableElevation endIcon={Icons.small.ChevronRightIcon} variant="contained" sx={{ marginTop: "5px", textTransform: "capitalize" }} size='small' color='error'>
                                                Continue
                                            </Button>}
                                            {profileProgress === 100 && <Button onClick={() => navigate( '/student/resume-builder' )} disableElevation endIcon={Icons.small.ChevronRightIcon} variant="contained" sx={{ marginTop: "5px", textTransform: "capitalize" }} size='small' color='error'>
                                                Get resume
                                            </Button>}
                                        </Box>
                                    </Box>
                                    <Box>
                                        <img src={resumeImg} alt="Complete your resume" />
                                    </Box>
                                </Box>
                            </Box>
                        </Card>

                    </Box>

                </Box>
                <Box display="flex" flexDirection="column" gap="20px">
                    <Box sx={{ display: { xs: "none", md: "block" } }}>
                        <SubjectsCard shadow />
                    </Box>
                    <DiscussionsCard />
                </Box>
            </Box> */}
        </Box >
    )
}
)
export default StudentDashboard


// const getCalendarEvents = useCallback( async () => {
//     try {
//         const events = {}
//         try {
//             const { data: { data: examEvents } } = await api.fetchExamEvents()
//             examEvents.forEach( event => {
//                 const date = formatDate( event.from_time, { month: "numeric", day: "numeric" } )
//                 if ( events[date] ) {
//                     if ( !events[date]["eventsPresent"].includes( "exam" ) )
//                         events[date]["eventsPresent"].push( "exam" )
//                     events[date]["events"].push( { eventType: "exam", details: { ...event, id: event.exam_id, title: event.exam_name, from: event.from_time, to: event.to_time } } )
//                 } else {
//                     events[date] = {}
//                     events[date]["eventsPresent"] = ["exam"]
//                     events[date]["events"] = [
//                         { eventType: "exam", details: { ...event, id: event.exam_id, title: event.exam_name, from: event.from_time, to: event.to_time } }
//                     ]
//                 }

//             } )
//         } catch ( err ) { }

//         try {
//             const { data: { data: assignmentEvents } } = await api.fetchAssignmentEvents()
//             assignmentEvents.forEach( event => {
//                 const date = formatDate( event.assignment_created_at, { month: "numeric", day: "numeric" } )
//                 if ( events[date] ) {
//                     if ( !events[date]["eventsPresent"].includes( "assignment" ) )
//                         events[date]["eventsPresent"].push( "assignment" )
//                     events[date]["events"].push( { eventType: "assignment", details: { ...event, title: event.assignment_name, from: event.assignment_created_at, to: event.assignment_due_date } } )
//                 } else {
//                     events[date] = {}
//                     events[date]["eventsPresent"] = ["assignment"]
//                     events[date]["events"] = [
//                         { eventType: "assignment", details: { ...event, title: event.assignment_name, from: event.assignment_created_at, to: event.assignment_due_date } }
//                     ]
//                 }

//             } )
//         } catch ( err ) { }
//         if ( RoomStore.getRoom.length > 0 ) {
//             for ( let i = 0; i < RoomStore.getRoom.length; i++ ) {
//                 const room = RoomStore.getRoom[i]
//                 const date = formatDate()
//                 if ( events[date] ) {
//                     if ( !events[date]["eventsPresent"].includes( "class" ) )
//                         events[date]["eventsPresent"].push( "class" )
//                     events[date]["events"].push( { eventType: "class", details: { room } } )
//                 } else {
//                     events[date] = {}
//                     events[date]["eventsPresent"] = ["class"]
//                     events[date]["events"] = [
//                         { eventType: "class", details: { room } }
//                     ]
//                 }
//             }
//         }
//         setCalendarEvents( events )
//     } catch ( err ) {

//     } finally {
//         setAreEventsLoaded( true )
//     }
// }, [RoomStore] )


/* <Card elevation={1}>
                            <Box borderRadius="10px" padding="20px" >
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography gutterBottom fontSize="20px" fontWeight="700">Attendance</Typography>
                                    <img width="50px" src={attendanceImg} alt="Attendance" />
                                </Box>
                                <Box padding="20px 0" display="flex" alignItems="center" justifyContent="space-between">
                                    <Progress size={70} progress={79} color="#FF6C8F" secondaryColor='#EDF0EE' />
                                    <Box>
                                        <Typography gutterBottom borderLeft="4px solid #EDF0EE" paddingLeft="10px" variant="subtitle2" textTransform="capitalize">Total</Typography>
                                        <Typography borderLeft="4px solid #FF6C8F" paddingLeft="10px" variant="subtitle2" textTransform="capitalize">Attendance</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Card> 
                        
                        
                         <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr" }} gap="20px">
                <Card
                    sx={{
                        flexShrink: "0",
                        background: theme === 'dark' ? palette.common.bg : `linear-gradient(328.48deg, #00A917 37.65%, #D5DF3A 92.12%)`,
                    }}>
                    <Box
                        borderRadius="10px"
                        flexGrow={1}
                        padding="20px"
                    >
                        <Typography textAlign={{ md: "left", xs: "center" }} variant="h6" gutterBottom fontSize="20px" color="#fff" fontWeight="700">Profile</Typography>
                        <Box flexWrap="wrap" display="flex" color="#fff" gap="30px" justifyContent="space-between" alignItems="center" >
                            <Box display="flex" flexDirection={{ md: "row", xs: "column" }} gap="20px" alignItems="center" >
                                <Avatar src={convertToCDNLink( UserStore.getUser.user_profile )} sx={{ width: "80px", height: "80px", borderRadius: "10px" }} />
                                <Box display="flex" flexGrow={1} flexDirection="column" alignItems={{ md: "initial", xs: "center" }} justifyContent="space-between" >
                                    <Typography textAlign={{ md: "left", xs: "center" }} fontSize="22px" fontWeight={500} >{UserStore.getUser.name || UserStore.getUser.user_name}</Typography>
                                    <Typography textAlign={{ md: "left", xs: "center" }} fontSize="12px">{UserStore.getUser?.branch} branch</Typography>
                                    <Typography textAlign={{ md: "left", xs: "center" }} fontSize="12px">{UserStore.getUser?.user_org_code}</Typography>
                                </Box>
                            </Box>
                            <Box display={{ md: "flex", sm: "none" }} flexDirection="column" gap="10px" alignItems="center" >
                                <img src={badgeImg} width="70px" alt="Badge" />
                                <Typography color="white" fontWeight="bold" sx={{ background: "linear-gradient(105.99deg, #139CFF -3.04%, #151D48 80.56%)" }} fontSize="10px" borderRadius="5px" padding="5px 20px">
                                    GRADE
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Card>
                <AcademicsProgressCard />
            </Box>
                        
                        */